// @/shared/components/MembershipInput.tsx
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormField } from "@/shared/components/FormField";
import { InputWithLabel } from "@/shared/components/ui/InputWithLabel";
import { Quote } from "@/shared/types/Quote.interface";
import { useAppLayerContext } from "../contexts/AppLayer";
import { Button } from "./ui";
import { useFormParentContext } from "../contexts/FormParent";
import { BaseFeatureComponentProps } from "./FeatureSlot/registry";
import { UIUtils } from "../utils/UIUtils";
import { StorageUtils } from "../utils/StorageUtils";
import { ResponsiveModalSheet } from "./ResponsiveModalSheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

export type MembershipInputMetadata = {
    organizationName: string;
    fieldName: string;
    labelText: string;
    removeMembershipLabel?: string;
    placeholder?: string;
    subText?: string;
    subTextSuperscript?: string;
    helpLink?: {
        text: string;
        modalTitle: string;
        modalContent: string; // HTML string
    };
};

type MembershipInputProps = BaseFeatureComponentProps<MembershipInputMetadata>;

export const MembershipInput: React.FC<MembershipInputProps> = ({ stepId, metadata, quote }) => {
    const { control, clearErrors, setValue, getValues } = useFormContext();
    const { setValue: setParentValue } = useFormParentContext<Quote>();
    const { appState, updateAppState } = useAppLayerContext();
    const { updateQuote, isQuoteUpdating } = appState;

    // State for modals
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

    const organizationName = metadata?.organizationName || "Membership";
    const fieldName = metadata?.fieldName || "membershipId";
    const labelText = metadata?.labelText || "Membership ID";
    const placeholder = metadata?.placeholder;
    const subText = metadata?.subText;
    const subTextSuperscript = metadata?.subTextSuperscript;
    const removeMembershipLabel = metadata?.removeMembershipLabel;
    const helpLink = metadata?.helpLink;

    const handleOpenRemoveConfirm = () => {
        if (isQuoteUpdating) return;
        setIsRemoveConfirmOpen(true);
    };

    const handleCloseRemoveConfirm = () => setIsRemoveConfirmOpen(false);

    const handleConfirmRemoval = async () => {
        clearErrors(fieldName);
        setValue(fieldName, "");

        // Update the quote with useHighestDiscount=false
        if (updateQuote && !!quote?.id) {
            updateAppState({ showFinalLoader: true, finalLoaderText: "Removing discount..." });
            try {
                const updatedQuote = { ...quote };
                updatedQuote.discountCode = "Spot";
                updatedQuote.affiliateCode = "Spot";

                await updateQuote.mutateAsync({
                    quote: updatedQuote,
                    updateType: "UPDATE",
                    queryParams: { useHighestDiscount: false },
                    skipQueryUpdate: true
                });

                const currentStepValues = getValues();
                let stringifiedValues: string | null = null;
                try {
                    stringifiedValues = JSON.stringify(currentStepValues);
                } catch (error) {
                    console.error("Failed to stringify current step values:", error);
                }
                if (stringifiedValues) {
                    StorageUtils.setItem(`previous-values-${stepId}`, stringifiedValues);
                }

                await UIUtils.scrollToTop(3000, 50);
                const currentUrl = new URL(window.location.href);
                const newUrlWithoutPcode = UIUtils.removeSearchParamCaseInsensitive(currentUrl, "pcode");
                window.location.href = newUrlWithoutPcode;
            } catch (error) {
                console.error("Failed to update quote with useHighestDiscount=false:", error);
                updateAppState({ showFinalLoader: false, finalLoaderText: "" });
                setIsRemoveConfirmOpen(false);
            }
        } else {
            await UIUtils.scrollToTop(3000, 50);
            const currentUrl = new URL(window.location.href);
            const newUrlWithoutPcode = UIUtils.removeSearchParamCaseInsensitive(currentUrl, "pcode");
            window.location.href = newUrlWithoutPcode;
        }
    };

    const handleOpenHelpModal = () => setIsHelpModalOpen(true);
    const handleCloseHelpModal = () => setIsHelpModalOpen(false);

    // Create remove confirmation trigger button
    const removeConfirmTriggerButton = removeMembershipLabel ? (
        <Button
            variant="outline"
            type="button"
            size="sm"
            endDecorator={<FontAwesomeIcon icon={faArrowRight} height={16} width={16} />}
            className="h-auto border border-stroke-primary bg-background-primary px-2 py-1 text-xs font-medium text-content-secondary shadow-sm"
            onClick={handleOpenRemoveConfirm}
            disabled={isQuoteUpdating}
        >
            {removeMembershipLabel}
        </Button>
    ) : null;

    // Create error action node
    const errorAction = removeConfirmTriggerButton ? (
        <ResponsiveModalSheet
            isOpen={isRemoveConfirmOpen}
            onClose={handleCloseRemoveConfirm}
            sheetSide="top"
            showCloseButton={!isRemoveConfirmOpen}
            triggerButton={removeConfirmTriggerButton}
            styles={{
                sheet: {
                    content: "flex flex-col pb-16",
                    title: "flex justify-between items-center"
                },
                modal: {
                    title: "flex justify-between items-center",
                    content: "flex flex-col !max-w-[400px]",
                    closeButton: "hidden"
                }
            }}
        >
            <div className="flex size-full flex-col items-center justify-center gap-6">
                <div className="text-center">
                    <h2 className="text-lg font-bold text-content-primary">Remove discount?</h2>
                    <p className="mt-2 text-left text-sm text-content-secondary">Are you sure you want to proceed without the {organizationName} discount?</p>
                </div>
                <div className="flex w-full gap-4">
                    <Button variant="ghost" onClick={handleCloseRemoveConfirm} className="flex-1" disabled={isQuoteUpdating}>
                        Cancel
                    </Button>
                    <Button variant="default" onClick={handleConfirmRemoval} className="flex-1" disabled={isQuoteUpdating} loading={isQuoteUpdating}>
                        {isQuoteUpdating ? "Removing..." : "Yes"}
                    </Button>
                </div>
            </div>
        </ResponsiveModalSheet>
    ) : undefined;

    // Create help trigger button
    const helpTriggerButton = helpLink ? (
        <Button
            variant="link"
            type="button"
            className="flex size-auto grow-0 justify-start p-0 text-left text-sm font-medium leading-5 text-content-secondary underline underline-offset-auto"
            onClick={handleOpenHelpModal}
        >
            {helpLink.text}
        </Button>
    ) : null;

    return (
        <>
            <Controller
                name={fieldName}
                control={control}
                render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                    <>
                        <FormField className="min-w-[250px]" error={error?.message} errorId={`error-${fieldName}`}>
                            <InputWithLabel
                                variant={"floating-inner"}
                                label={labelText}
                                inputRef={ref}
                                error={error?.message}
                                inputProps={{
                                    ...rest,
                                    placeholder: placeholder,
                                    autoComplete: "off",
                                    "aria-describedby": error ? `error-${fieldName}` : undefined,
                                    maxLength: 25
                                }}
                            />
                        </FormField>
                        {(!!subText || !!helpLink) && (
                            <div className="mt-1 flex flex-col gap-1">
                                {!!subText && (
                                    <span className="text-sm font-medium leading-5 text-content-secondary">
                                        {subText}
                                        {subTextSuperscript && <sup>{subTextSuperscript}</sup>}
                                    </span>
                                )}
                                {helpLink && helpTriggerButton && (
                                    <ResponsiveModalSheet
                                        isOpen={isHelpModalOpen}
                                        onClose={handleCloseHelpModal}
                                        sheetSide="bottom"
                                        triggerButton={helpTriggerButton}
                                        showCloseButton={true}
                                        styles={{
                                            sheet: {
                                                content: "flex flex-col max-h-[calc(100vh-80px)]"
                                            },
                                            modal: {
                                                content: "flex flex-col max-w-[600px]",
                                                closeButton: "hidden"
                                            }
                                        }}
                                    >
                                        <div className="max-w-none text-content-primary" dangerouslySetInnerHTML={{ __html: helpLink.modalContent }} />
                                    </ResponsiveModalSheet>
                                )}
                            </div>
                        )}
                        {!!errorAction && !!error && <div className="mt-5">{errorAction}</div>}
                    </>
                )}
            />
        </>
    );
};
